/**
 * 처방 확정 제외 사유 선택 모달.
 * 중복·잘못 올라온 이미지를 확정 대상에서 빼고, 앱에는 '제외'로 표시되게 한다.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import type { PrescriptionImageExclusionReason } from '@/entities/prescription/api/prescription.api';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/components/ui/form';
import {
  popupPrimaryButtonClassName,
  popupSecondaryButtonClassName,
} from '@/shared/components/ui/popup-styles';
import { RadioGroup, RadioGroupItem } from '@/shared/components/ui/radio-group';
import { PRESCRIPTION_EXCLUSION_REASON_OPTIONS } from '@/pages/prescriptions/prescriptionUtils';

const exclusionSchema = z.object({
  reason: z.enum(['DUPLICATE_IMAGE', 'INVALID_IMAGE'], {
    message: '제외 사유를 선택해주세요',
  }),
});

type ExclusionFormValues = z.infer<typeof exclusionSchema>;

interface PrescriptionExclusionDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 이미 제외된 이미지면 현재 사유를 기본 선택 (재지정 시 사유 갱신) */
  defaultReason?: PrescriptionImageExclusionReason | null;
  submitting: boolean;
  onSubmit: (reason: PrescriptionImageExclusionReason) => void;
}

export const PrescriptionExclusionDialog: React.FC<
  PrescriptionExclusionDialogProps
> = ({ open, onOpenChange, defaultReason, submitting, onSubmit }) => {
  const form = useForm<ExclusionFormValues>({
    resolver: zodResolver(exclusionSchema),
    defaultValues: { reason: defaultReason ?? undefined },
  });

  useEffect(() => {
    if (open) form.reset({ reason: defaultReason ?? undefined });
  }, [open, defaultReason, form]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>처방 확정 제외</DialogTitle>
          <DialogDescription>
            제외된 처방은 확정되지 않고 처방 장수 집계에서 빠집니다. 앱에는
            그대로 보이며 &apos;제외&apos;로 표시됩니다.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            id="prescription-exclusion-form"
            onSubmit={form.handleSubmit((values) => onSubmit(values.reason))}
          >
            <FormField
              control={form.control}
              name="reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>제외 사유</FormLabel>
                  <FormControl>
                    <RadioGroup
                      value={field.value ?? ''}
                      onValueChange={field.onChange}
                      className="flex flex-col gap-3"
                    >
                      {PRESCRIPTION_EXCLUSION_REASON_OPTIONS.map((opt) => (
                        <label
                          key={opt.value}
                          htmlFor={`exclusion-reason-${opt.value}`}
                          className="flex cursor-pointer items-center gap-2 text-sm"
                        >
                          <RadioGroupItem
                            value={opt.value}
                            id={`exclusion-reason-${opt.value}`}
                          />
                          {opt.label}
                        </label>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
        <DialogFooter>
          <Button
            type="button"
            className={popupSecondaryButtonClassName}
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            취소
          </Button>
          <Button
            type="submit"
            form="prescription-exclusion-form"
            className={popupPrimaryButtonClassName}
            disabled={submitting}
          >
            제외
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
